import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Root } from 'react-dom/client';
import * as csc from 'cornerstone-core';
import * as cst from 'cornerstone-tools';
import { useIntl } from 'react-intl';

import RotationAngleSelectorOverlay, {
  normalizeAngle,
} from './EmbeddedToolsButtons/RotationAngleSelectorOverlay';
import { forceImageUpdate } from '../../CornerstoneTools/forceImageUpdate';
import { focusHandles } from 'app/CornerstoneTools/CropTool';
import { EventWithCornerstoneDetail } from 'app/CornerstoneTools/types';
import styled from 'styled-components';
import { createPortal } from 'react-dom';

const FreeRotationOverlayPortal = styled.div`
  pointer-events: none;
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: repeating-linear-gradient(
      to right,
      rgba(255, 255, 255, 0.2) 0px,
      rgba(255, 255, 255, 0.2) 1px,
      transparent 1px,
      transparent calc(100% / 8)
    ),
    repeating-linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0.2) 0px,
      rgba(255, 255, 255, 0.2) 1px,
      transparent 1px,
      transparent calc(100% / 6)
    );
  background-size: 100% 100%;
`;

type FreeRotationToolButtonProps = {
  isFreeRotationActive: boolean;
  toggleFreeRotation: (shouldActivate: boolean) => void;
  setRotationAngle: (angle: number) => void;
  focusedElement: HTMLElement; // Cornerstone element to retrieve current rotation value
};

interface FreeRotationOverlayContainer extends HTMLDivElement {
  root?: Root;
}

interface FocusedElementWithOverlay extends HTMLElement {
  FreeRotationOverlayContainer?: FreeRotationOverlayContainer;
}

const FreeRotationToolButton: React.FC<FreeRotationToolButtonProps> = ({
  isFreeRotationActive,
  toggleFreeRotation,
  setRotationAngle,
  focusedElement,
}) => {
  const intl = useIntl();
  const [rotation, setRotation] = useState<number | undefined>(undefined);
  const initialRotationRef = useRef<number>(0);

  const getCurrentRotation = useCallback((): number => {
    if (!focusedElement) return 0;
    try {
      const { viewport } = csc.getEnabledElement(focusedElement);
      return normalizeAngle(viewport.rotation || 0);
    } catch {
      return 0;
    }
  }, [focusedElement]);

  const setViewportRotation = useCallback(
    (angle: number): void => {
      if (!focusedElement) return;
      try {
        const { viewport } = csc.getEnabledElement(focusedElement);
        viewport.rotation = angle;
        const cropToolData = cst.getToolState(focusedElement, 'Crop');
        if (cropToolData && cropToolData.data.length > 0) {
          focusHandles(focusedElement, cropToolData.data[0].handles);
        }
        forceImageUpdate(focusedElement);
      } catch (error) {
        console.log('Error in FreeRotationToolButton setViewportRotation', error);
      }
    },
    [focusedElement]
  );

  const handleRotationChange = useCallback(
    (angle: number): void => {
      setViewportRotation(angle);
    },
    [setViewportRotation]
  );

  const handleValidate = useCallback(
    (angle: number): void => {
      setViewportRotation(angle);
      setRotationAngle(angle);
      toggleFreeRotation(false);
    },
    [setViewportRotation, setRotationAngle, toggleFreeRotation]
  );

  const handleCancel = useCallback((): void => {
    setViewportRotation(initialRotationRef.current);
    toggleFreeRotation(false);
  }, [setViewportRotation, toggleFreeRotation]);

  useEffect(() => {
    if (!focusedElement) return;

    const onViewportUpdated = (evt: EventWithCornerstoneDetail) => {
      const newRotation = normalizeAngle(evt.detail?.viewport?.rotation);
      setRotation((prev) => (prev === newRotation ? prev : newRotation));
    };

    focusedElement.addEventListener(csc.EVENTS.IMAGE_RENDERED, onViewportUpdated as EventListener);

    return () => {
      focusedElement.removeEventListener(
        csc.EVENTS.IMAGE_RENDERED,
        onViewportUpdated as EventListener
      );
    };
  }, [isFreeRotationActive, focusedElement]);

  const handleClick = useCallback((): void => {
    if (isFreeRotationActive) {
      handleCancel();
    } else {
      initialRotationRef.current = getCurrentRotation();
      toggleFreeRotation(true);
    }
  }, [isFreeRotationActive, handleCancel, toggleFreeRotation, getCurrentRotation]);

  const activeClass = isFreeRotationActive ? 'active' : '';

  return (
    <button
      type="button"
      className={`picoxia tool-button ${activeClass}`}
      onClick={handleClick}
      title={intl.formatMessage({ id: 'tools.free_rotation.tooltip' })}
    >
      <i className="svg-icon free-rotation" />
      {isFreeRotationActive &&
        focusedElement &&
        createPortal(
          <FreeRotationOverlayPortal>
            <RotationAngleSelectorOverlay
              intl={intl}
              initialRotation={initialRotationRef.current}
              onRotationChange={handleRotationChange}
              onValidate={handleValidate}
              onCancel={handleCancel}
              rotation={rotation}
            />
          </FreeRotationOverlayPortal>,
          focusedElement
        )}
    </button>
  );
};

export default FreeRotationToolButton;
